import React from 'react';
import Countdown from './Countdown';
import ProgressBar from './ProgressBar';
import { formatCurrency, formatDate, getTripTotals, getTripStatus, getMotivationalMessage, getDaysUntil, getPerMonth, TRIP_TYPES } from '../utils/helpers';

const statusStyles = {
  'all-paid': { bg: 'bg-green-50 border-green-200', badge: 'bg-green-100 text-green-700', label: 'All paid! ✓' },
  'overdue': { bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700', label: 'Overdue' },
  'due-soon': { bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700', label: 'Payment due soon' },
  'upcoming': { bg: 'bg-purple-50 border-purple-200', badge: 'bg-purple-100 text-purple-700', label: 'On track' },
};

export default function HolidayCard({ holiday, onSelect, onAddPayment, isHero, members }) {
  const { totalCost, totalPaid, outstanding, progress } = getTripTotals(holiday);
  const status = getTripStatus(holiday);
  const style = statusStyles[status];
  const isDream = holiday.status === 'dream';
  const days = getDaysUntil(holiday.departureDate);
  const motivation = getMotivationalMessage(holiday);

  const tripMembers = members.filter(m => holiday.familyMembers.includes(m.id));
  const tripType = TRIP_TYPES.find(t => t.id === holiday.tripType);
  const dreamTarget = holiday.totalBudget || 0;

  return (
    <div
      className={`rounded-2xl border-2 overflow-hidden card-hover cursor-pointer ${isHero ? 'col-span-full' : ''} ${style.bg}`}
      onClick={() => onSelect(holiday.id)}
    >
      {isHero && (
        <div className="bg-gradient-to-r from-purple-600 via-teal-500 to-coral-400 text-white px-4 py-2 text-center text-sm font-bold tracking-wide">
          ✨ NEXT ADVENTURE ✨
        </div>
      )}

      <div className={`p-5 ${isHero ? 'md:flex md:items-center md:gap-6' : ''}`}>
        {/* Left: Emoji & Info */}
        <div className={`${isHero ? 'md:flex-1' : ''}`}>
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className={`${isHero ? 'text-5xl' : 'text-3xl'}`}>{holiday.emoji}</span>
              <div>
                <h3 className={`font-bold text-gray-800 ${isHero ? 'text-2xl' : 'text-lg'}`}>
                  {holiday.destination}
                </h3>
                <div className="flex gap-1 flex-wrap mt-0.5">
                  {tripType && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                      {tripType.icon} {tripType.label}
                    </span>
                  )}
                  {isDream && (
                    <span className="text-xs bg-sunny-200 text-sunny-700 px-2 py-0.5 rounded-full font-medium">
                      Dream Trip ✨
                    </span>
                  )}
                  {holiday.status === 'planning' && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      Planning
                    </span>
                  )}
                </div>
              </div>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${style.badge}`}>
              {style.label}
            </span>
          </div>

          {/* Members */}
          {tripMembers.length > 0 && (
            <div className="flex gap-1 mb-3">
              {tripMembers.map(m => (
                <span
                  key={m.id}
                  className="text-xs px-2 py-0.5 rounded-full text-white font-medium"
                  style={{ backgroundColor: m.colour }}
                >
                  {m.name}
                </span>
              ))}
            </div>
          )}

          {/* Financials */}
          <div className="mb-3">
            {isDream ? (
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Saved: <strong>{formatCurrency(totalPaid)}</strong></span>
                  <span className="text-gray-600">Target: <strong>{formatCurrency(dreamTarget)}</strong></span>
                </div>
                <ProgressBar progress={dreamTarget > 0 ? (totalPaid / dreamTarget) * 100 : 0} />
              </div>
            ) : (
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Paid: <strong>{formatCurrency(totalPaid)}</strong></span>
                  <span className="text-gray-600">Total: <strong>{formatCurrency(totalCost)}</strong></span>
                </div>
                <ProgressBar progress={progress} />
                {outstanding > 0 && (
                  <div className="text-sm font-semibold text-coral-500 mt-1">
                    {formatCurrency(outstanding)} outstanding
                    {holiday.paymentDeadline && (
                      <span className="text-gray-500 font-normal"> · due by {formatDate(holiday.paymentDeadline)}</span>
                    )}
                  </div>
                )}
                {outstanding > 0 && holiday.paymentDeadline && (() => {
                  const pm = getPerMonth(outstanding, holiday.paymentDeadline);
                  if (!pm) return null;
                  return (
                    <div className="text-sm text-purple-600 font-semibold mt-0.5">
                      {pm.months > 0
                        ? `💷 ${formatCurrency(pm.amount)}/month ${pm.label}`
                        : `💷 ${formatCurrency(pm.amount)} — ${pm.label}!`
                      }
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Motivational message */}
          <p className="text-sm text-gray-500 italic">{motivation}</p>
        </div>

        {/* Right: Countdown */}
        {days > 0 && (
          <div className={`${isHero ? 'md:text-center md:px-8' : 'mt-4 pt-4 border-t border-gray-200/50'}`}>
            <Countdown date={holiday.departureDate} large={isHero} />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-5 pb-4 flex gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); onSelect(holiday.id); }}
          className="flex-1 py-2 px-4 bg-white/80 hover:bg-white rounded-xl text-sm font-semibold text-purple-600 transition-colors border border-purple-200"
        >
          View Details
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onAddPayment(holiday.id); }}
          className="flex-1 py-2 px-4 bg-gradient-to-r from-purple-500 to-teal-500 hover:from-purple-600 hover:to-teal-600 rounded-xl text-sm font-semibold text-white transition-colors"
        >
          + Add Payment
        </button>
      </div>
    </div>
  );
}
